import type { ComponentProps } from 'react';

import { cn } from '../cn';

/*
 * From the Brand Kit's Properties page.
 * https://www.figma.com/design/9vWZFxa4lVRC3uwqMOzrG2/Endaoment-Design-System-2.0?node-id=75-185
 *
 * Fixed artwork rather than currentColor: colour is what tells the properties
 * apart. The frame's edge reads `--frame`, with the Kit's tint as the
 * fallback, so a card can set it to the card's own edge with an arbitrary
 * property (`[--frame:...]`). Not the text colour: the door cards draw the
 * icon's hover ring with `ring-current`, and a colour class on the svg would
 * take that over from the door. The outlines are stroked six times heavier
 * than the export, because stroke scales with the viewBox and at 1008px the
 * exported 4 lands under a fifth of a pixel. Ids are namespaced, or two on a
 * page paint the same.
 */
type PropertyIconProps = ComponentProps<'svg'>;

const PERIWINKLE_FRAME = 'var(--frame, color-mix(in srgb, var(--color-periwinkle) 25%, transparent))';
const PURPLE_FRAME = 'var(--frame, color-mix(in srgb, var(--color-purple) 25%, transparent))';

export const AppIcon = ({ className, ...props }: PropertyIconProps) => (
  <svg
    viewBox='0 0 1008 1008'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    role='img'
    aria-label='Endaoment App'
    className={cn('size-5', className)}
    {...props}>
    <g>
      <rect x='2' y='2' width='1004' height='1004' rx='202' fill='white' />
      <rect x='2' y='2' width='1004' height='1004' rx='202' stroke={PERIWINKLE_FRAME} strokeWidth='24' />
      <g>
        <g clipPath='url(#app-clip0_75_185)'>
          <path
            d='M462 144C462.051 144.132 504 251.421 504 287.4C504 308.066 501.779 320.512 492.498 345.6C490.36 351.38 487.959 357.742 485.417 364.39C464.126 420.081 420.081 464.126 364.39 485.417C357.742 487.959 351.38 490.36 345.6 492.498C320.512 501.779 308.066 504 287.4 504C251.414 504 144.08 462.031 144 462L170.4 366C204.541 375.932 238.944 385.564 271.189 394.387C346.732 415.056 415.056 346.733 394.387 271.19C385.564 238.944 375.932 204.541 366 170.4L462 144ZM288 224.4C323.125 224.4 351.6 253.009 351.6 288.3C351.6 323.591 323.125 352.2 288 352.2C252.875 352.2 224.4 323.591 224.4 288.3C224.4 253.009 252.875 224.4 288 224.4Z'
            fill='url(#app-paint0_linear_75_185)'
          />
        </g>
        <g clipPath='url(#app-clip1_75_185)'>
          <path
            d='M546 144C545.949 144.132 504 251.421 504 287.4C504 308.066 506.221 320.512 515.502 345.6C517.64 351.38 520.041 357.742 522.583 364.39C543.874 420.081 587.919 464.126 643.61 485.417C650.258 487.959 656.62 490.36 662.4 492.498C687.488 501.779 699.934 504 720.6 504C756.586 504 863.92 462.031 864 462L837.6 366C803.459 375.932 769.056 385.564 736.811 394.387C661.268 415.056 592.944 346.733 613.613 271.19C622.436 238.944 632.068 204.541 642 170.4L546 144ZM720 224.4C684.875 224.4 656.4 253.009 656.4 288.3C656.4 323.591 684.875 352.2 720 352.2C755.125 352.2 783.6 323.591 783.6 288.3C783.6 253.009 755.125 224.4 720 224.4Z'
            fill='url(#app-paint1_linear_75_185)'
          />
        </g>
        <g clipPath='url(#app-clip2_75_185)'>
          <path
            d='M462 864C462.051 863.868 504 756.579 504 720.6C504 699.934 501.779 687.488 492.498 662.4C490.36 656.62 487.959 650.258 485.417 643.61C464.126 587.919 420.081 543.874 364.39 522.583C357.742 520.041 351.38 517.64 345.6 515.502C320.512 506.221 308.066 504 287.4 504C251.414 504 144.08 545.968 144 546L170.4 642C204.541 632.068 238.944 622.436 271.189 613.613C346.732 592.944 415.056 661.267 394.387 736.81C385.564 769.056 375.932 803.459 366 837.6L462 864ZM288 783.6C323.125 783.6 351.6 754.991 351.6 719.7C351.6 684.409 323.125 655.8 288 655.8C252.875 655.8 224.4 684.409 224.4 719.7C224.4 754.991 252.875 783.6 288 783.6Z'
            fill='url(#app-paint2_linear_75_185)'
          />
        </g>
        <g clipPath='url(#app-clip3_75_185)'>
          <path
            d='M546 864C545.949 863.868 504 756.579 504 720.6C504 699.934 506.221 687.488 515.502 662.4C517.64 656.62 520.041 650.258 522.583 643.61C543.874 587.919 587.919 543.874 643.61 522.583C650.258 520.041 656.62 517.64 662.4 515.502C687.488 506.221 699.934 504 720.6 504C756.586 504 863.92 545.968 864 546L837.6 642C803.459 632.068 769.056 622.436 736.811 613.613C661.268 592.944 592.944 661.267 613.613 736.81C622.436 769.056 632.068 803.459 642 837.6L546 864ZM720 783.6C684.875 783.6 656.4 754.991 656.4 719.7C656.4 684.409 684.875 655.8 720 655.8C755.125 655.8 783.6 684.409 783.6 719.7C783.6 754.991 755.125 783.6 720 783.6Z'
            fill='url(#app-paint3_linear_75_185)'
          />
        </g>
      </g>
    </g>
    <defs>
      <linearGradient id='app-paint0_linear_75_185' x1='324' y1='144' x2='324' y2='504' gradientUnits='userSpaceOnUse'>
        <stop stopColor='#FF6D18' />
        <stop offset='1' stopColor='#F9BF01' />
      </linearGradient>
      <linearGradient id='app-paint1_linear_75_185' x1='684' y1='144' x2='684' y2='504' gradientUnits='userSpaceOnUse'>
        <stop stopColor='#829DFF' />
        <stop offset='1' stopColor='#4CC3FA' />
      </linearGradient>
      <linearGradient id='app-paint2_linear_75_185' x1='324' y1='504' x2='324' y2='864' gradientUnits='userSpaceOnUse'>
        <stop stopColor='#FF5E3E' />
        <stop offset='1' stopColor='#FF8A5B' />
      </linearGradient>
      <linearGradient id='app-paint3_linear_75_185' x1='684' y1='504' x2='684' y2='864' gradientUnits='userSpaceOnUse'>
        <stop stopColor='#A686FE' />
        <stop offset='1' stopColor='#E985FF' />
      </linearGradient>
      <clipPath id='app-clip0_75_185'>
        <rect width='360' height='360' fill='white' transform='translate(144 144)' />
      </clipPath>
      <clipPath id='app-clip1_75_185'>
        <rect width='360' height='360' fill='white' transform='matrix(-1 0 0 1 864 144)' />
      </clipPath>
      <clipPath id='app-clip2_75_185'>
        <rect width='360' height='360' fill='white' transform='matrix(1 0 0 -1 144 864)' />
      </clipPath>
      <clipPath id='app-clip3_75_185'>
        <rect width='360' height='360' fill='white' transform='matrix(-1 0 0 -1 864 864)' />
      </clipPath>
    </defs>
  </svg>
);

/** The construction grid is part of this mark, not a stray guide. */
export const ApiIcon = ({ className, ...props }: PropertyIconProps) => (
  <svg
    viewBox='0 0 1012 1012'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    role='img'
    aria-label='Endaoment API'
    className={cn('size-5', className)}
    {...props}>
    <g>
      <rect x='6' y='6' width='1000' height='1000' rx='200' fill='white' />
      <rect x='3' y='3' width='1006' height='1006' rx='203' stroke={PERIWINKLE_FRAME} strokeWidth='36' />
      <line opacity='0.25' x1='1009.01' y1='259' x2='9.005' y2='259' stroke='#627EEA' strokeWidth='6' />
      <line opacity='0.25' x1='756.005' y1='1006' x2='756.005' y2='6' stroke='#627EEA' strokeWidth='6' />
      <g>
        <rect opacity='0.25' x='6' y='3' width='1006' height='1006' rx='503' stroke='#627EEA' strokeWidth='6' />
        <line opacity='0.25' x1='509.005' y1='6' x2='509.005' y2='1006' stroke='#627EEA' strokeWidth='6' />
        <line opacity='0.25' x1='1006' y1='509.005' x2='6' y2='509.005' stroke='#627EEA' strokeWidth='6' />
        <line opacity='0.25' x1='1009.01' y1='759' x2='9.005' y2='759' stroke='#627EEA' strokeWidth='6' />
        <line opacity='0.25' x1='256.005' y1='1006' x2='256.005' y2='6' stroke='#627EEA' strokeWidth='6' />
      </g>
      <g>
        <path
          d='M289.4 506C310.066 506 322.512 508.221 347.6 517.502C353.38 519.64 359.742 522.041 366.39 524.583C422.081 545.874 466.126 589.919 487.417 645.61C489.959 652.258 492.36 658.62 494.498 664.4C503.779 689.488 506 701.935 506 722.601C506 758.579 464.051 865.868 464 866L368 839.601C377.932 805.459 387.564 771.056 396.387 738.811C417.056 663.268 348.732 594.944 273.189 615.613C240.944 624.436 206.541 634.068 172.4 644L146 548C146.08 547.969 253.414 506 289.4 506ZM722.6 506C758.586 506 865.92 547.969 866 548L839.6 644C805.459 634.068 771.056 624.436 738.811 615.613C663.268 594.944 594.944 663.268 615.613 738.811C624.436 771.056 634.068 805.459 644 839.601L548 866C547.949 865.868 506 758.579 506 722.601C506 701.935 508.221 689.488 517.502 664.4C519.64 658.62 522.041 652.258 524.583 645.61C545.874 589.919 589.919 545.874 645.61 524.583C652.258 522.041 658.62 519.64 664.4 517.502C689.488 508.221 701.934 506 722.6 506ZM290 657.8C325.125 657.8 353.6 686.409 353.6 721.7C353.6 756.991 325.125 785.601 290 785.601C254.875 785.601 226.4 756.991 226.4 721.7C226.4 686.409 254.875 657.8 290 657.8ZM722 657.8C757.125 657.8 785.6 686.409 785.6 721.7C785.599 756.991 757.125 785.601 722 785.601C686.875 785.601 658.401 756.991 658.4 721.7C658.4 686.409 686.875 657.8 722 657.8ZM464 146C464.051 146.132 506 253.421 506 289.399C506 310.065 503.779 322.512 494.498 347.6C492.36 353.38 489.959 359.742 487.417 366.39C466.126 422.081 422.081 466.126 366.39 487.417C359.742 489.959 353.38 492.36 347.6 494.498C322.512 503.779 310.066 506 289.4 506C253.414 506 146.08 464.031 146 464L172.4 368C206.541 377.932 240.944 387.564 273.189 396.387C348.732 417.056 417.056 348.732 396.387 273.189C387.564 240.944 377.932 206.541 368 172.399L464 146ZM644 172.399C634.068 206.541 624.436 240.944 615.613 273.189C594.944 348.732 663.268 417.056 738.811 396.387C771.056 387.564 805.459 377.932 839.6 368L866 464C865.92 464.031 758.586 506 722.6 506C701.934 506 689.488 503.779 664.4 494.498C658.62 492.36 652.258 489.959 645.61 487.417C589.919 466.126 545.874 422.081 524.583 366.39C522.041 359.742 519.64 353.38 517.502 347.6C508.221 322.512 506 310.065 506 289.399C506 253.421 547.949 146.132 548 146L644 172.399ZM290 226.399C325.125 226.399 353.6 255.009 353.6 290.3C353.6 325.591 325.125 354.2 290 354.2C254.875 354.2 226.4 325.591 226.4 290.3C226.4 255.009 254.875 226.399 290 226.399ZM722 226.399C757.125 226.399 785.599 255.009 785.6 290.3C785.6 325.591 757.125 354.2 722 354.2C686.875 354.2 658.4 325.591 658.4 290.3C658.401 255.009 686.875 226.399 722 226.399Z'
          fill='url(#api-paint0_linear_75_185)'
        />
      </g>
    </g>
    <defs>
      {/* Built from tokens, so this mark and the hue the property wears elsewhere cannot drift. */}
      <linearGradient id='api-paint0_linear_75_185' x1='506' y1='146' x2='506' y2='866' gradientUnits='userSpaceOnUse'>
        <stop stopColor='var(--color-blue)' />
        <stop offset='1' stopColor='var(--color-teal)' />
      </linearGradient>
    </defs>
  </svg>
);

export const GivingServicesIcon = ({ className, ...props }: PropertyIconProps) => (
  <svg
    viewBox='0 0 1008 1008'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    role='img'
    aria-label='Endaoment Giving Services'
    className={cn('size-5', className)}
    {...props}>
    <g>
      <rect x='2' y='2' width='1004' height='1004' rx='202' fill='white' />
      <rect x='2' y='2' width='1004' height='1004' rx='202' stroke={PURPLE_FRAME} strokeWidth='24' />
      <g clipPath='url(#gs-clip0_75_185)'>
        <path
          d='M504 665.4C504 674.875 505.018 680.581 509.272 692.082C510.253 694.732 511.354 697.649 512.52 700.696C522.281 726.228 542.473 746.42 568.004 756.181C571.052 757.346 573.968 758.446 576.618 759.427C588.12 763.682 593.826 764.7 603.3 764.7C593.826 764.7 588.12 765.718 576.618 769.973C573.968 770.953 571.052 772.055 568.004 773.22C542.473 782.981 522.281 803.173 512.52 828.704C511.354 831.752 510.253 834.668 509.272 837.318C505.018 848.82 504 854.526 504 864C504 854.526 502.982 848.82 498.728 837.318C497.747 834.668 496.646 831.752 495.48 828.704C485.719 803.173 465.527 782.981 439.996 773.22C436.948 772.055 434.032 770.953 431.382 769.973C419.88 765.718 414.174 764.7 404.7 764.7C414.174 764.7 419.88 763.682 431.382 759.427C434.032 758.446 436.948 757.346 439.996 756.181C465.527 746.42 485.719 726.228 495.48 700.696C496.646 697.649 497.747 694.732 498.728 692.082C502.982 680.581 504 674.875 504 665.4ZM504.082 144C702.95 144 864.164 305.214 864.164 504.082C864.164 617.795 811.453 719.195 729.133 785.186V729.136C729.133 660.602 698.498 599.223 650.176 557.945C615.336 599.834 562.824 626.503 504.082 626.503C445.34 626.503 392.828 599.834 357.987 557.945C309.665 599.223 279.03 660.602 279.03 729.136V785.186C196.711 719.195 144 617.795 144 504.082C144 305.214 305.214 144 504.082 144ZM504.082 301.536C429.507 301.536 369.051 361.991 369.051 436.566C369.051 511.142 429.507 571.598 504.082 571.598C578.657 571.598 639.112 511.142 639.112 436.566C639.112 361.991 578.657 301.536 504.082 301.536Z'
          fill='url(#gs-paint0_linear_75_185)'
        />
      </g>
    </g>
    <defs>
      <linearGradient
        id='gs-paint0_linear_75_185'
        x1='803.319'
        y1='504'
        x2='803.319'
        y2='1102.34'
        gradientUnits='userSpaceOnUse'>
        <stop stopColor='#8679E1' />
        <stop offset='1' stopColor='#F38AFE' />
      </linearGradient>
      <clipPath id='gs-clip0_75_185'>
        <rect width='720' height='720' fill='white' transform='translate(144 144)' />
      </clipPath>
    </defs>
  </svg>
);
